/** DLR2 v2 routing graph, read piecemeal over HTTP ranges.
 *
 * The graph is the same file the phone downloads whole and mmaps
 * (ios/Dustlark/Plan/Router.swift is the reference reader; the writer is
 * factory/dustlark_factory/stages/graph.py). Layout, little-endian:
 *
 *   header (32 B): magic "DLR2", u32 version=2, u32 nVerts, u32 nEdges,
 *                  u32 nWays, u32 nStrings, u32 stringBytesLen, u32 0
 *   verts    nVerts x 8B   (f32 lon, f32 lat)
 *   offsets  (nVerts+1) x 4B u32       — CSR row offsets into edges
 *   edges    nEdges x 12B  (u32 target, f32 meters, u32 wayId)
 *   ways     nWays x 8B    (u8 classMask, u8 flags, u16 seasonClosureId,
 *                           u32 nameId)
 *   strOffs  (nStrings+1) x 4B u32     — offsets into strBytes
 *   strBytes raw UTF-8, index 0 = ""
 *
 * Access pattern: ways + strings are tiny (a few MB) and fetched eagerly
 * at open; verts/offsets/edges are read through a block-cached RangeSource
 * because A* only touches a corridor of the graph. Vertex ids cluster
 * along ways (insertion order), so block caching is effective. */

import type { LonLat } from "./types";

/** Random-access byte source. Implementations: HTTP Range, in-memory. */
export interface RangeSource {
  read(offset: number, length: number): Promise<ArrayBuffer>;
  size(): Promise<number>;
}

export function httpRangeSource(url: string): RangeSource {
  let sizePromise: Promise<number> | null = null;

  async function fetchSize(): Promise<number> {
    const head = await fetch(url, { method: "HEAD" });
    const len = head.headers.get("content-length");
    if (head.ok && len != null) {
      const n = Number(len);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    // Some servers/proxies don't answer HEAD with a usable content-length —
    // ask for one byte instead and read the total off Content-Range.
    const r = await fetch(url, { headers: { Range: "bytes=0-0" } });
    const range = r.headers.get("content-range"); // "bytes 0-0/12345"
    const m = range ? /\/(\d+)$/.exec(range) : null;
    if (r.status === 206 && m) return Number(m[1]);
    throw new Error(
      `httpRangeSource: could not determine size of ${url} (HEAD and ranged GET both failed)`,
    );
  }

  return {
    async read(offset: number, length: number): Promise<ArrayBuffer> {
      if (length <= 0) return new ArrayBuffer(0);
      const end = offset + length - 1;
      const r = await fetch(url, { headers: { Range: `bytes=${offset}-${end}` } });
      if (r.status !== 206) {
        // A 200 means the server ignored the Range header and is about to
        // hand back the WHOLE file (190 MB+ at Utah scale) — fail loudly
        // rather than silently buffering it.
        throw new Error(
          `httpRangeSource: ${url} returned ${r.status} for Range bytes=${offset}-${end} ` +
            `(expected 206 Partial Content — server may not support range requests)`,
        );
      }
      return r.arrayBuffer();
    },
    size(): Promise<number> {
      if (!sizePromise) sizePromise = fetchSize();
      return sizePromise;
    },
  };
}

/** For tests: a RangeSource over an in-memory buffer. */
export function memorySource(data: ArrayBuffer | Uint8Array): RangeSource {
  // Re-wrap as a bare Uint8Array view (same bytes, no copy) rather than
  // trusting `data` as given: Node's Buffer (what fs.readFileSync
  // returns, and a Uint8Array subclass) overrides .slice() to return a
  // VIEW into its own possibly-pooled backing ArrayBuffer instead of
  // copying — .buffer on that view would then be the whole shared pool,
  // not this file's bytes. Constructing a plain Uint8Array here restores
  // Uint8Array.prototype.slice's normal copy-into-a-fresh-buffer behavior
  // for every read() below.
  const bytes =
    data instanceof Uint8Array ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength) : new Uint8Array(data);
  return {
    async read(offset: number, length: number): Promise<ArrayBuffer> {
      // .slice() (not .subarray) copies into a fresh, tightly-sized
      // buffer — callers never see bytes outside [offset, offset+length)
      // even when `bytes` is itself a view into a larger buffer.
      return bytes.slice(offset, offset + length).buffer;
    },
    async size(): Promise<number> {
      return bytes.byteLength;
    },
  };
}

/** Block-aligned LRU cache in front of another source — 256 KB blocks,
 * capped; concurrent reads of one block dedupe to one upstream fetch. */
export function blockCached(
  src: RangeSource,
  blockSize = 256 * 1024,
  maxBlocks = 96,
): RangeSource {
  // Map iteration order = insertion order, so a delete+re-set on every
  // touch keeps it in least-recently-used → most-recently-used order; the
  // first key is always the LRU eviction candidate.
  const blocks = new Map<number, ArrayBuffer>();
  const inFlight = new Map<number, Promise<ArrayBuffer>>();
  let sizePromise: Promise<number> | null = null;

  function fileSize(): Promise<number> {
    if (!sizePromise) sizePromise = src.size();
    return sizePromise;
  }

  async function fetchBlock(blockIndex: number): Promise<ArrayBuffer> {
    const total = await fileSize();
    const start = blockIndex * blockSize;
    // Final block is clamped to what's actually left in the source.
    const len = Math.max(0, Math.min(blockSize, total - start));
    const buf = len > 0 ? await src.read(start, len) : new ArrayBuffer(0);
    console.debug(`blockCached: fetched block ${blockIndex} (${buf.byteLength} B)`);
    return buf;
  }

  function loadBlock(blockIndex: number): Promise<ArrayBuffer> {
    const cached = blocks.get(blockIndex);
    if (cached) {
      blocks.delete(blockIndex);
      blocks.set(blockIndex, cached); // refresh MRU position
      return Promise.resolve(cached);
    }
    let pending = inFlight.get(blockIndex);
    if (!pending) {
      pending = fetchBlock(blockIndex)
        .then((buf) => {
          blocks.set(blockIndex, buf);
          if (blocks.size > maxBlocks) {
            const oldest = blocks.keys().next().value;
            if (oldest !== undefined) blocks.delete(oldest);
          }
          return buf;
        })
        .finally(() => inFlight.delete(blockIndex));
      inFlight.set(blockIndex, pending);
    }
    return pending;
  }

  return {
    async read(offset: number, length: number): Promise<ArrayBuffer> {
      if (length <= 0) return new ArrayBuffer(0);
      const out = new Uint8Array(length);
      let filled = 0;
      let pos = offset;
      const end = offset + length;
      while (pos < end) {
        const blockIndex = Math.floor(pos / blockSize);
        const blockStart = blockIndex * blockSize;
        const block = new Uint8Array(await loadBlock(blockIndex));
        const withinBlock = pos - blockStart;
        const available = block.byteLength - withinBlock;
        if (available <= 0) {
          throw new Error(
            `blockCached: read past end of source at offset ${pos} ` +
              `(block ${blockIndex} has ${block.byteLength} B)`,
          );
        }
        const take = Math.min(available, end - pos);
        out.set(block.subarray(withinBlock, withinBlock + take), filled);
        filled += take;
        pos += take;
      }
      return out.buffer;
    },
    size: fileSize,
  };
}

export interface WayAttr {
  classMask: number;
  flags: number;
  seasonClosureId: number;
  nameId: number;
}

export interface Edge {
  target: number;
  meters: number;
  wayId: number;
}

const HEADER_BYTES = 32;
const VERTEX_BYTES = 8;
const OFFSET_BYTES = 4;
const EDGE_BYTES = 12;
const WAY_BYTES = 8;
const STR_OFFSET_BYTES = 4;

interface Sections {
  vertsBase: number;
  offsetsBase: number;
  edgesBase: number;
}

function readMagic(view: DataView): string {
  return String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
}

export class DLR2Graph {
  readonly nVerts: number = 0;
  readonly nEdges: number = 0;
  readonly nWays: number = 0;

  private readonly src: RangeSource;
  private readonly sections: Sections;
  private readonly waysView: DataView;
  private readonly strOffsets: Uint32Array;
  private readonly strBytes: Uint8Array;
  private static readonly decoder = new TextDecoder("utf-8");

  private constructor(
    src: RangeSource,
    nVerts: number,
    nEdges: number,
    nWays: number,
    sections: Sections,
    waysBuf: ArrayBuffer,
    strOffsets: Uint32Array,
    strBytes: Uint8Array,
  ) {
    this.src = src;
    this.nVerts = nVerts;
    this.nEdges = nEdges;
    this.nWays = nWays;
    this.sections = sections;
    this.waysView = new DataView(waysBuf);
    this.strOffsets = strOffsets;
    this.strBytes = strBytes;
  }

  static async open(src: RangeSource): Promise<DLR2Graph> {
    const headerBuf = await src.read(0, HEADER_BYTES);
    const header = new DataView(headerBuf);
    const magic = readMagic(header);
    if (magic === "DLRG") {
      throw new Error(
        "DLR2Graph.open: v1 DLRG graph — browser router requires DLR2 v2 (rebake this region's routing.bin)",
      );
    }
    if (magic !== "DLR2") {
      throw new Error(`DLR2Graph.open: bad magic ${JSON.stringify(magic)} (expected "DLR2")`);
    }
    const version = header.getUint32(4, true);
    if (version !== 2) {
      throw new Error(`DLR2Graph.open: unsupported DLR2 version ${version} (expected 2)`);
    }
    const nVerts = header.getUint32(8, true);
    const nEdges = header.getUint32(12, true);
    const nWays = header.getUint32(16, true);
    const nStrings = header.getUint32(20, true);
    const strBytesLen = header.getUint32(24, true);

    const vertsBase = HEADER_BYTES;
    const offsetsBase = vertsBase + nVerts * VERTEX_BYTES;
    const edgesBase = offsetsBase + (nVerts + 1) * OFFSET_BYTES;
    const waysBase = edgesBase + nEdges * EDGE_BYTES;
    const strOffsBase = waysBase + nWays * WAY_BYTES;
    const strBytesBase = strOffsBase + (nStrings + 1) * STR_OFFSET_BYTES;
    const totalBytes = strBytesBase + strBytesLen;

    // Bound-check every section against the real source length before
    // accepting it — a truncated way/string table must throw here, never
    // misparse a short read later.
    const fileSize = await src.size();
    if (fileSize < totalBytes) {
      throw new Error(
        `DLR2Graph.open: truncated source — sections need ${totalBytes} B, source has ${fileSize} B`,
      );
    }

    // Ways + strings are tiny (a few MB at Utah scale) — fetch eagerly so
    // way()/wayName() never round-trip the network; verts/offsets/edges
    // stay behind the (block-cached) RangeSource since A* only ever walks
    // a corridor of those.
    const [waysBuf, strOffsBuf, strBytesBuf] = await Promise.all([
      src.read(waysBase, nWays * WAY_BYTES),
      src.read(strOffsBase, (nStrings + 1) * STR_OFFSET_BYTES),
      src.read(strBytesBase, strBytesLen),
    ]);

    const strOffsView = new DataView(strOffsBuf);
    const strOffsets = new Uint32Array(nStrings + 1);
    for (let i = 0; i <= nStrings; i++) {
      strOffsets[i] = strOffsView.getUint32(i * STR_OFFSET_BYTES, true);
    }

    console.debug(
      `DLR2Graph.open: ${nVerts} verts, ${nEdges} edges, ${nWays} ways, ${nStrings} strings ` +
        `(${totalBytes} B total, ${waysBuf.byteLength + strOffsBuf.byteLength + strBytesBuf.byteLength} B fetched eagerly)`,
    );

    return new DLR2Graph(
      src,
      nVerts,
      nEdges,
      nWays,
      { vertsBase, offsetsBase, edgesBase },
      waysBuf,
      strOffsets,
      new Uint8Array(strBytesBuf),
    );
  }

  /** [lon, lat] of a vertex (ranged read, block-cached). */
  async vertex(id: number): Promise<LonLat> {
    if (id < 0 || id >= this.nVerts) {
      throw new RangeError(`DLR2Graph.vertex: id ${id} out of range [0, ${this.nVerts})`);
    }
    const buf = await this.src.read(this.sections.vertsBase + id * VERTEX_BYTES, VERTEX_BYTES);
    const v = new DataView(buf);
    return [v.getFloat32(0, true), v.getFloat32(4, true)];
  }

  /** Outgoing edges of a vertex (two ranged reads: CSR row, then edges). */
  async edges(id: number): Promise<Edge[]> {
    if (id < 0 || id >= this.nVerts) {
      throw new RangeError(`DLR2Graph.edges: id ${id} out of range [0, ${this.nVerts})`);
    }
    // offsets[id] and offsets[id+1] are adjacent u32s — one read covers
    // the whole CSR row.
    const rowBuf = await this.src.read(this.sections.offsetsBase + id * OFFSET_BYTES, OFFSET_BYTES * 2);
    const row = new DataView(rowBuf);
    const start = row.getUint32(0, true);
    const end = row.getUint32(4, true);
    if (end <= start) return [];

    const buf = await this.src.read(this.sections.edgesBase + start * EDGE_BYTES, (end - start) * EDGE_BYTES);
    const view = new DataView(buf);
    const out: Edge[] = new Array(end - start);
    for (let i = 0; i < end - start; i++) {
      const off = i * EDGE_BYTES;
      out[i] = {
        target: view.getUint32(off, true),
        meters: view.getFloat32(off + 4, true),
        wayId: view.getUint32(off + 8, true),
      };
    }
    return out;
  }

  /** Fire-and-forget warm-up for a vertex's edges: reads the CSR row and
   * then the edge bytes through this same (block-cached) source — the
   * exact two reads edges() itself performs — so a later real edges(id)
   * call is a cache hit instead of a fresh ranged read. Sequential ranged
   * reads are the wall-clock cost of a long A* solve; astar.ts calls this
   * after relaxing a node for the heap's current top few candidates, so
   * the network wait for the likely-next expansions overlaps ongoing
   * compute instead of stalling it, without changing expansion order —
   * the prefetch result is never consumed, only cached. A guess, not a
   * commitment: errors (including an out-of-range id) are swallowed, and
   * the method itself never throws or returns anything to await. */
  prefetchEdges(id: number): void {
    void this.prefetchEdgesAsync(id).catch(() => {});
  }

  private async prefetchEdgesAsync(id: number): Promise<void> {
    if (id < 0 || id >= this.nVerts) return;
    const rowBuf = await this.src.read(this.sections.offsetsBase + id * OFFSET_BYTES, OFFSET_BYTES * 2);
    const row = new DataView(rowBuf);
    const start = row.getUint32(0, true);
    const end = row.getUint32(4, true);
    if (end <= start) return;
    await this.src.read(this.sections.edgesBase + start * EDGE_BYTES, (end - start) * EDGE_BYTES);
  }

  /** Way attributes / name from the eagerly-loaded tables. */
  way(wayId: number): WayAttr {
    if (wayId < 0 || wayId >= this.nWays) {
      throw new RangeError(`DLR2Graph.way: id ${wayId} out of range [0, ${this.nWays})`);
    }
    const off = wayId * WAY_BYTES;
    return {
      classMask: this.waysView.getUint8(off),
      flags: this.waysView.getUint8(off + 1),
      seasonClosureId: this.waysView.getUint16(off + 2, true),
      nameId: this.waysView.getUint32(off + 4, true),
    };
  }

  wayName(wayId: number): string {
    const nameId = this.way(wayId).nameId;
    // Index 0 is the reserved "" slot (unnamed ways point there).
    if (nameId <= 0 || nameId >= this.strOffsets.length - 1) return "";
    const start = this.strOffsets[nameId];
    const end = this.strOffsets[nameId + 1];
    return DLR2Graph.decoder.decode(this.strBytes.subarray(start, end));
  }
}
